import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Sparkles } from 'lucide-react';
import { isThemeSoundEnabled, setThemeSoundEnabled, playThemeSound } from '../../../utils/themeAudio';

const HOLIDAY_WISHES = [
    '🎄 MERRY CHRISTMAS! May your gaming holidays be legendary!',
    '🎁 HOLIDAY SURPRISE! Extra cheer & joy on all store items!',
    '⭐ SLEIGH BELLS RING! The holiday magic is alive!',
    '🦌 SANTA IS FLYING BY! Check your cart for holiday perks!',
    '❄️ WARM WINTER WISHES! Unstoppable victories this season!'
];

const HolidayGiftBox = () => {
    const [soundOn, setSoundOn] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [wishMessage, setWishMessage] = useState('');
    const [isOpened, setIsOpened] = useState(false);
    const [flyingSanta, setFlyingSanta] = useState(false);
    const wishTimeoutRef = useRef(null);

    useEffect(() => {
        setSoundOn(isThemeSoundEnabled());
    }, []);

    const toggleSound = (e) => {
        e.stopPropagation();
        const next = !soundOn;
        setSoundOn(next);
        setThemeSoundEnabled(next);
        if (next) {
            playThemeSound('christmas', 'jingle');
        }
    };

    const handleUnwrap = () => {
        setIsOpened(true);
        setTimeout(() => setIsOpened(false), 800);

        // Sound FX
        playThemeSound('christmas', 'giftPop');

        // Launch Flying Santa across sky
        setFlyingSanta(true);
        setTimeout(() => setFlyingSanta(false), 5500);

        // Random Wish Message
        const wish = HOLIDAY_WISHES[Math.floor(Math.random() * HOLIDAY_WISHES.length)];
        setWishMessage(wish);
        if (wishTimeoutRef.current) clearTimeout(wishTimeoutRef.current);
        wishTimeoutRef.current = setTimeout(() => setWishMessage(''), 3500);
    };

    return (
        <>
            {/* Flying Santa Sleigh Easter Egg */}
            {flyingSanta && (
                <div className="fixed top-12 md:top-20 -left-64 z-[75] pointer-events-none select-none animate-sleigh-fly flex items-center gap-2">
                    <span className="text-2xl md:text-3xl filter drop-shadow-[0_4px_12px_rgba(251,191,36,0.8)]">
                        🦌 🦌 🦌 🛷 🎅 ✨
                    </span>
                </div>
            )}

            {/* Global Holiday Banner Popup */}
            {wishMessage && (
                <div className="fixed top-16 md:top-20 inset-x-0 flex justify-center z-[75] pointer-events-none px-4">
                    <div className="w-full max-w-md animate-bounce">
                    <div className="bg-gradient-to-r from-red-600 via-green-600 to-red-600 text-white font-bold text-center px-5 py-3 rounded-2xl shadow-2xl shadow-green-950/80 border border-yellow-400/60 backdrop-blur-md flex items-center justify-center gap-2">
                        <Sparkles className="w-5 h-5 text-yellow-300 animate-spin" />
                        <span className="text-sm tracking-wide text-shadow">{wishMessage}</span>
                    </div>
                </div>
                </div>
            )}

            {/* Bottom-left Gift Mascot */}
            <div className="fixed bottom-5 left-4 md:left-8 z-[68] select-none flex flex-col items-start gap-2">
                {/* Speech Tooltip */}
                {hovered && !wishMessage && (
                    <div className="bg-gray-950/95 text-yellow-300 text-xs font-semibold px-3 py-1.5 rounded-xl border border-yellow-500/50 shadow-lg shadow-black/60 animate-bounce whitespace-nowrap mb-1">
                        Unwrap your holiday gift! 🎁
                        <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-950 border-b border-r border-yellow-500/50 rotate-45" />
                    </div>
                )}

                <div className="flex items-center gap-2">
                    {/* The Interactive Gift Box */}
                    <button
                        type="button"
                        onClick={handleUnwrap}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        className={`group relative outline-none focus:outline-none transition-all duration-300 ${
                            isOpened ? 'animate-spooky-shake scale-125' : 'animate-float hover:scale-110'
                        }`}
                        title="Click to unwrap holiday cheer!"
                    >
                        {/* Golden Holiday Glow */}
                        <div className="absolute inset-0 rounded-2xl bg-red-500/30 blur-xl group-hover:bg-yellow-400/40 transition-all duration-500 pointer-events-none" />

                        {/* Gift Box SVG */}
                        <svg width="54" height="54" viewBox="0 0 100 100" className="relative drop-shadow-[0_4px_16px_rgba(220,38,38,0.7)]">
                            {/* Box Body */}
                            <rect x="15" y="38" width="70" height="52" rx="8" fill="#dc2626" stroke="#991b1b" strokeWidth="2" />
                            {/* Box Lid */}
                            <rect x="10" y="28" width="80" height="16" rx="4" fill="#ef4444" stroke="#991b1b" strokeWidth="2" />

                            {/* Vertical Gold Ribbon */}
                            <rect x="44" y="28" width="12" height="62" fill="#fbbf24" stroke="#d97706" strokeWidth="1" />
                            {/* Horizontal Gold Ribbon */}
                            <rect x="15" y="58" width="70" height="12" fill="#fbbf24" stroke="#d97706" strokeWidth="1" />

                            {/* Gold Bow on Top */}
                            {/* Left Loop */}
                            <path d="M 50 28 C 30 10 20 25 45 28 Z" fill="#f59e0b" stroke="#b45309" strokeWidth="1.5" />
                            {/* Right Loop */}
                            <path d="M 50 28 C 70 10 80 25 55 28 Z" fill="#f59e0b" stroke="#b45309" strokeWidth="1.5" />
                            {/* Center Knot */}
                            <circle cx="50" cy="28" r="5" fill="#fde047" stroke="#d97706" strokeWidth="1.5" />

                            {/* Sparkles on Box */}
                            <circle cx="28" cy="48" r="1.5" fill="#ffffff" opacity="0.8" />
                            <circle cx="72" cy="78" r="1.5" fill="#ffffff" opacity="0.8" />
                        </svg>
                    </button>

                    {/* Sound Toggle Pill */}
                    <button
                        type="button"
                        onClick={toggleSound}
                        className="px-2.5 py-1.5 rounded-full bg-gray-900/90 hover:bg-gray-800 text-yellow-400 border border-yellow-500/40 text-[11px] font-bold flex items-center gap-1.5 shadow-md shadow-black/40 transition-all hover:scale-105 active:scale-95"
                        title={soundOn ? 'Holiday Audio Enabled (Click to Mute)' : 'Enable Holiday Sleigh Audio'}
                    >
                        {soundOn ? (
                            <>
                                <Volume2 className="w-3.5 h-3.5 text-yellow-400 animate-pulse" />
                                <span className="hidden sm:inline">Audio ON</span>
                            </>
                        ) : (
                            <>
                                <VolumeX className="w-3.5 h-3.5 text-gray-400" />
                                <span className="hidden sm:inline text-gray-400">Audio OFF</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default HolidayGiftBox;
